/**
 * The sign-up email code step on phones (23 Oct 2026, design "Check your email"). Clerk's own step
 * cannot be restyled this far, so this one takes its place at /sign-up/verify-email-address and talks
 * to the same Clerk sign-up: the code is still sent and checked by Clerk. Every other sign-up step
 * stays Clerk's form.
 *
 *   ‹  learnbee
 *   [✉]  Check your email
 *        We sent a 6-digit code to <email> Change
 *        Verification code  [_][_][_] – [_][_][_]   (checked as soon as the sixth digit is in)
 *        Didn't get it? Resend code in 0:20
 *        [ Verify email ]
 *        Can't find it? Check your spam or promotions folder.
 *                                Have an organisation code? Join with code
 */
import { useEffect, useRef, useState } from 'react'
import { useSignUp } from '@clerk/clerk-react'
import { isClerkAPIResponseError } from '@clerk/clerk-react/errors'
import { useNavigate } from 'react-router-dom'
import { ChevronLeft, Mail, AlertCircle } from 'lucide-react'

const C = { ink: '#16141C', body: '#3F3B47', muted: '#6B6775', border: '#DDD8E4', purple: '#6741D9', tint: '#EFEAFB', red: '#B42318' }
const RESEND_AFTER = 30

export function EmailCodeStep({ redirectTo }: { redirectTo: string }) {
  const { isLoaded, signUp, setActive } = useSignUp()
  const navigate = useNavigate()
  const [code, setCode] = useState('')
  const [err, setErr] = useState('')
  const [busy, setBusy] = useState(false)
  const [wait, setWait] = useState(RESEND_AFTER)
  const [focused, setFocused] = useState(true)
  const input = useRef<HTMLInputElement>(null)
  const email = signUp?.emailAddress ?? ''

  // Nothing to verify (a refresh lost the attempt, or the address was never entered): back to the form.
  useEffect(() => { if (isLoaded && !email) navigate('/sign-up', { replace: true }) }, [isLoaded, email, navigate])
  useEffect(() => {
    if (wait <= 0) return
    const t = setTimeout(() => setWait(w => w - 1), 1000)
    return () => clearTimeout(t)
  }, [wait])

  const back = () => navigate('/sign-up')
  const verify = async (entered = code) => {
    if (!signUp || entered.length !== 6 || busy) return
    setBusy(true); setErr('')
    try {
      const r = await signUp.attemptEmailAddressVerification({ code: entered })
      if (r.status === 'complete') {
        await setActive({ session: r.createdSessionId })
        navigate(redirectTo, { replace: true })
        return
      }
      // Something else to fill in (a name, a password): Clerk's own form carries on.
      navigate('/sign-up/continue')
    } catch (e) {
      const c = isClerkAPIResponseError(e) ? e.errors[0]?.code : ''
      setErr(c === 'form_code_incorrect' ? 'That code isn’t right. Check the email and try again.'
        : c === 'verification_expired' ? 'That code has expired. Resend a new one.'
        : c === 'verification_failed' ? 'Too many tries. Resend a new code.'
        : isClerkAPIResponseError(e) ? e.errors[0]?.longMessage || e.errors[0]?.message || 'That didn’t work. Try again.'
        : 'That didn’t work. Try again.')
      setBusy(false)
    }
  }
  const resend = async () => {
    if (!signUp || wait > 0) return
    setErr(''); setCode('')
    try { await signUp.prepareEmailAddressVerification({ strategy: 'email_code' }); setWait(RESEND_AFTER); input.current?.focus() }
    catch (e) { setErr(isClerkAPIResponseError(e) ? e.errors[0]?.longMessage || 'Couldn’t send a new code.' : 'Couldn’t send a new code.') }
  }

  const ready = code.length === 6 && !busy
  const slot = (i: number) => {
    const ch = code[i]
    const active = focused && !err && i === Math.min(code.length, 5) && code.length < 6
    return (
      <div key={i} style={{ height: 58, borderRadius: 12, display: 'grid', placeItems: 'center', fontSize: 24, fontWeight: 600, color: C.ink,
        border: `1.5px solid ${err ? C.red : active || ch ? C.purple : C.border}`, boxShadow: active ? `0 0 0 4px ${C.tint}` : 'none', background: '#fff' }}>
        {ch ?? (active ? <span style={{ width: 2, height: 24, background: C.purple }} className="animate-pulse" /> : '')}
      </div>
    )
  }

  return (
    <div style={{ minHeight: '100dvh', background: '#fff', display: 'flex', flexDirection: 'column', padding: '0 24px', color: C.ink }}>
      <header style={{ position: 'relative', height: 64, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <button onClick={back} aria-label="Back" style={{ position: 'absolute', left: -8, width: 44, height: 44, border: 'none', background: 'none', display: 'grid', placeItems: 'center', cursor: 'pointer', color: C.body }}>
          <ChevronLeft size={22} />
        </button>
        <img src="/learnbee-logo.png" alt="Learnbee" style={{ height: 30, width: 'auto' }} />
      </header>

      <main style={{ display: 'flex', flexDirection: 'column', gap: 0, paddingTop: 28 }}>
        <span aria-hidden style={{ width: 52, height: 52, borderRadius: 14, background: C.tint, color: C.purple, display: 'grid', placeItems: 'center' }}><Mail size={22} /></span>
        <h1 style={{ margin: '22px 0 0', fontSize: 28, fontWeight: 700, letterSpacing: '-.01em' }}>Check your email</h1>
        <p style={{ margin: '10px 0 0', fontSize: 15.5, lineHeight: 1.5, color: C.body }}>
          We sent a 6-digit code to<br />
          <strong style={{ color: C.ink, wordBreak: 'break-all' }}>{email}</strong>{' '}
          <button onClick={back} style={{ border: 'none', background: 'none', padding: 0, color: C.purple, fontSize: 15.5, textDecoration: 'underline', cursor: 'pointer' }}>Change</button>
        </p>

        <label htmlFor="lb-email-code" style={{ marginTop: 26, fontSize: 14.5, fontWeight: 500, color: C.body }}>Verification code</label>
        <div style={{ position: 'relative', marginTop: 10 }} onClick={() => input.current?.focus()}>
          <div aria-hidden style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 14px 1fr 1fr 1fr', gap: 8, alignItems: 'center' }}>
            {slot(0)}{slot(1)}{slot(2)}
            <span style={{ height: 2, background: C.border, borderRadius: 1 }} />
            {slot(3)}{slot(4)}{slot(5)}
          </div>
          {/* One real input over the boxes: typing, pasting, deleting and the phone's code
              suggestion (autocomplete one-time-code) all behave normally. */}
          <input id="lb-email-code" ref={input} value={code} autoFocus inputMode="numeric" autoComplete="one-time-code" maxLength={6}
            // The sixth digit checks it straight away — typed, pasted or picked from the keyboard's
            // suggestion (23 Oct 2026); the button stays for a retry.
            onChange={e => { const v = e.target.value.replace(/\D/g, '').slice(0, 6); setCode(v); setErr(''); if (v.length === 6) void verify(v) }}
            onKeyDown={e => { if (e.key === 'Enter') void verify() }}
            onFocus={() => setFocused(true)} onBlur={() => setFocused(false)}
            aria-invalid={!!err} aria-describedby={err ? 'lb-email-code-err' : undefined}
            style={{ position: 'absolute', inset: 0, width: '100%', opacity: 0, fontSize: 16, cursor: 'text' }} />
        </div>

        {err && (
          <p id="lb-email-code-err" role="alert" style={{ margin: '12px 0 0', display: 'flex', gap: 8, fontSize: 14.5, lineHeight: 1.4, color: C.red }}>
            <AlertCircle size={18} style={{ flex: 'none', marginTop: 1 }} />{err}
          </p>
        )}
        <p style={{ margin: '12px 0 0', fontSize: 14.5, color: C.muted }}>
          Didn’t get it?{' '}
          {wait > 0
            ? <>Resend code in <strong style={{ color: C.ink, fontVariantNumeric: 'tabular-nums' }}>0:{String(wait).padStart(2, '0')}</strong></>
            : <button onClick={() => void resend()} style={{ border: 'none', background: 'none', padding: 0, color: C.purple, fontSize: 14.5, fontWeight: 600, textDecoration: 'underline', cursor: 'pointer' }}>Resend code</button>}
        </p>

        <button onClick={() => void verify()} disabled={!ready}
          style={{ marginTop: 26, height: 56, borderRadius: 14, border: 'none', fontSize: 17, fontWeight: 600, cursor: ready ? 'pointer' : 'default',
            background: ready ? C.purple : '#E7E2F6', color: ready ? '#fff' : '#8E87A8', boxShadow: ready ? '0 8px 20px rgba(103,65,217,.22)' : 'none' }}>
          {busy ? 'Verifying…' : 'Verify email'}
        </button>
        <p style={{ margin: '22px 0 0', fontSize: 14, color: C.muted }}>Can’t find it? Check your spam or promotions folder.</p>
      </main>

      <footer style={{ marginTop: 'auto', padding: '28px 0 24px', textAlign: 'center', fontSize: 14.5, color: C.body }}>
        Have an organisation code?{' '}
        <button onClick={back} style={{ border: 'none', background: 'none', padding: 0, color: C.purple, fontSize: 14.5, textDecoration: 'underline', cursor: 'pointer' }}>Join with code</button>
      </footer>
    </div>
  )
}
